import React, { useState, useEffect } from 'react';
import { Box, useTheme } from '@mui/material';
import { ObjectGroupList, ObjectGroupType } from '@europrocurement/flexy-components';
import type { IconName } from '@fortawesome/fontawesome-svg-core';
import {
    FilterOptionValue,
    FlexyFilterValue,
    FlexyFilterWidgetProps,
    StaticSelectFilter,
} from '../DatatableHeader';
import { FlexyFilterSearchInput } from '../FlexyFilterSearchInput';

type FlexySelectFilterProps = Omit<FlexyFilterWidgetProps, 'filter'> & {
    filter: StaticSelectFilter;
};

export const FlexySelectFilter: React.FunctionComponent = function (props: FlexySelectFilterProps) {
    const { filter, value, onChange } = props;
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectValue, setSelectValue] = useState<FlexyFilterValue | null>(null);
    const theme = useTheme();

    useEffect(() => {
        setSelectValue(value);
    }, [value]);

    const handleChange = (objectGroup: ObjectGroupType) => {
        setSelectValue(objectGroup.id);
        onChange(String(objectGroup.id));
    };

    const listItemIconToDisplay: IconName | undefined = React.useMemo(() => {
        if (!filter.icon && !filter.renderFilterListIcon) return undefined;

        if (!filter.renderFilterListIcon && filter.icon) {
            return filter.icon;
        }

        if (filter.renderFilterListIcon) {
            return filter.renderFilterListIcon;
        }

        return undefined;
    }, [filter.icon, filter.renderFilterListIcon]);

    const options = filter.options
        .filter((option: FilterOptionValue) =>
            option.label.toLowerCase().match(searchTerm.toLowerCase()),
        )
        .map((option: FilterOptionValue) => ({
            id: option.value as unknown as number,
            type: filter.field,
            label: filter.renderFilterList ? filter.renderFilterList(option.value) : option.label,
            icone: listItemIconToDisplay,
        }));

    return (
        <Box sx={{ width: '100%', padding: '10px' }}>
            <FlexyFilterSearchInput
                placeholder={filter.label}
                searchTerm={searchTerm}
                onSearchTermUpdate={(term) => setSearchTerm(term)}
            />
            <Box
                sx={{
                    padding: '5px 0',
                    overflowY: 'scroll',
                    maxHeight: '220px',
                }}
            >
                <Box
                    sx={{
                        color: theme.palette.text.primary,
                        padding: '0px 14px',
                        fontSize: '14px',
                        fontWeight: '500',
                        lineHeight: '25.2px',
                        textAlign: 'left',
                        marginBottom: '5px',
                    }}
                >
                    {filter.label}
                </Box>
                <ObjectGroupList
                    selected={[selectValue as number]}
                    objectGroups={options}
                    onClick={handleChange}
                />
            </Box>
        </Box>
    );
};
